import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { filterMenuByRole } from '../constants/menuConfig';

/**
 * Custom hook to get filtered and memoized menu items based on user context
 * Performance optimized with useMemo to prevent unnecessary re-renders
 * Optimized: Added custom equality checks to all useSelector calls
 * 
 * @param {Array} menuConfig - Menu configuration array
 * @returns {Array} Filtered menu items for current user
 * 
 * @example
 * const menuItems = useMemoizedMenu(MENU_CONFIG);
 */
export const useMemoizedMenu = (menuConfig) => {
  // Get user context from Redux with custom equality checks
  const userType = useSelector(
    (state) =>
      state?.userType?.type !== 'Other'
        ? state?.userType?.type
        : state?.userDetails?.details?.Title,
    (prev, next) => prev === next
  );

  const salesGroupCode = useSelector(
    (state) => state?.userDetails?.details?.Sales_Group_Code__c,
    (prev, next) => prev === next
  );

  const selectedSalesGroupCode = useSelector(
    (state) =>
      state?.userDetails?.details?.Sales_Group_Code__c !== null
        ? state?.userDetails?.details?.Sales_Group_Code__c
        : state?.SelectedSalesGroupCode?.SalesGroupCode,
    (prev, next) => prev === next
  );

  const userDivision = useSelector(
    (state) => state?.userDetails?.details?.Division,
    (prev, next) => prev === next
  );

  const HO_Role__c = useSelector(
    (state) => state?.userType?.type,
    (prev, next) => prev === next
  );

  // Memoize user context to prevent unnecessary filtering
  const userContext = useMemo(
    () => ({
      userType,
      salesGroupCode,
      selectedSalesGroupCode,
      userDivision,
      HO_Role__c,
    }),
    [userType, salesGroupCode, selectedSalesGroupCode, userDivision, HO_Role__c]
  );

  // Memoize filtered menu items
  const filteredMenu = useMemo(
    () => filterMenuByRole(menuConfig, userContext),
    [menuConfig, userContext]
  );

  return filteredMenu;
};
